import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatLabel } from '../DocumentImport/importCitation';
import { useSourceViewer } from '../DocumentImport/SourceViewerContext';
import ImportUploadModal from '../DocumentImport/ImportUploadModal';
import OutsideChatPromptChooser from '../DocumentImport/OutsideChatPromptChooser';
import { documentImportApi } from '../../api/documentImport';
import { formatFileWordCount } from '../../utils/docListDetail';
import { docsCharacterOffersPath } from '../../utils/docsCharacterOffers';
import { NOTES_EMPTY, NOTES_LEDE } from '../../utils/seriesNotes';
import { publishChatFocus } from '../Chat/ChatPaneFrame';
import { publishChatAttachment } from '../Chat/chatAttachment';
import {
  BRAINSTORM_IN_CHAT,
  UPLOAD_A_DOC,
  BRING_STORY_FROM_ANOTHER_CHAT,
  BRING_STORY_PROMPT_ACCEPT_NOTICE,
  BRING_STORY_SHEET_UPLOAD_HINT,
  COPY_FRAMEWORK_MAPPING_ADDON,
  COPY_FRAMEWORK_MAPPING_ADDON_DESC,
  MAKE_NOTES_FROM_DOCS,
  OUTSIDE_CHAT_ADDON_PROMPT,
} from '../../utils/writerCopy';
import BookToolsGroup from '../BookTools/BookToolsGroup';
import './SeriesNotes.css';

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) { /* clipboard/permission — silent */ }
  return false;
}

function batchStatus(batch, approvedIds) {
  if (batch.approved || approvedIds?.has(batch.batch_id)) return 'Digest approved';
  return 'In the pool — digest not approved yet';
}

function batchHasDigest(batch) {
  if (!batch) return false;
  if (batch.generated === true) return true;
  if (typeof batch.characters === 'string' && batch.characters.trim()) return true;
  if (typeof batch.scope_note === 'string' && batch.scope_note.trim()) return true;
  return false;
}

function digestToApprovePayload(digest) {
  const text = (value) => (value == null ? '' : String(value));
  const optional = (value) => {
    const trimmed = text(value).trim();
    return trimmed || null;
  };
  return {
    characters: text(digest?.characters),
    plot_threads: text(digest?.plot_threads),
    world_details: text(digest?.world_details),
    theme: optional(digest?.theme),
    logline: optional(digest?.logline),
    scope_note: optional(digest?.scope_note),
  };
}

export default function SeriesNotes({
  project,
  storyId = null,
  batches = [],
  loading = false,
  error = null,
  onRefresh,
  onImportNext,
  uploadLabel = null,
  bookTools = false,
}) {
  const [showUpload, setShowUpload] = useState(false);
  const [bringOpen, setBringOpen] = useState(false);
  const [copied, setCopied] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [approvedIds, setApprovedIds] = useState(() => new Set());
  const [approveError, setApproveError] = useState(null);
  const { openImportSourceByChunkId } = useSourceViewer();

  const copyPrompt = async (text, which) => {
    const ok = await copyText(text);
    if (ok) setCopied(which);
  };

  const approveBatch = async (batch) => {
    if (!project?.id || !batch?.batch_id || approvingId) return;
    if (batch.approved || approvedIds.has(batch.batch_id)) return;
    setApprovingId(batch.batch_id);
    setApproveError(null);
    try {
      // Free re-entry. Read these docs stays paid generate and does not approve.
      // Fetch the stored digest so approve does not blank fields the pile omits.
      const existing = await documentImportApi.getDigest(project.id, batch.batch_id);
      await documentImportApi.approveDigest(
        project.id,
        batch.batch_id,
        digestToApprovePayload(existing),
      );
      setApprovedIds((prev) => {
        const next = new Set(prev);
        next.add(batch.batch_id);
        return next;
      });
      try {
        const refresh = onRefresh?.();
        if (refresh && typeof refresh.then === 'function') await refresh;
      } catch {
        // Approval already landed. The pile refresh is the follow-up read.
      }
    } catch (err) {
      setApproveError({
        batchId: batch.batch_id,
        message: err?.message || 'Could not approve the digest.',
      });
    } finally {
      setApprovingId(null);
    }
  };

  const closeUpload = (payload) => {
    setShowUpload(false);
    const go = () => onImportNext?.(payload);
    let refresh;
    try {
      refresh = onRefresh?.();
    } catch {
      refresh = null;
    }
    if (refresh && typeof refresh.then === 'function') {
      refresh.then(go, go);
      return;
    }
    go();
  };

  return (
    <section className="sn" aria-labelledby="sn-heading">
      <div className="sn__intro">
        {bookTools ? (
          <div className="sn__title-row">
            <h2 id="sn-heading" className="sn__heading">Docs</h2>
            <BookToolsGroup />
          </div>
        ) : (
          <h2 id="sn-heading" className="sn__heading">Docs</h2>
        )}
        <p className="sn__lede">{NOTES_LEDE}</p>
      </div>

      <div className="sn__bring">
        <button
          type="button"
          className="sn__bring-toggle"
          aria-expanded={bringOpen}
          onClick={() => setBringOpen((open) => !open)}
        >
          <span className="sn__bring-caret" aria-hidden="true">▸</span>
          {BRING_STORY_FROM_ANOTHER_CHAT}
        </button>
        {bringOpen && (
          <div className="sn__bring-body">
            <p className="sn__bring-desc">{BRING_STORY_PROMPT_ACCEPT_NOTICE}</p>
            <OutsideChatPromptChooser />
            <div className="sn__bring-row">
              <div className="sn__bring-actions">
                <button
                  type="button"
                  className="sn__bring-copy"
                  onClick={() => copyPrompt(OUTSIDE_CHAT_ADDON_PROMPT, 'addon')}
                >
                  {COPY_FRAMEWORK_MAPPING_ADDON}
                </button>
                {copied === 'addon' && (
                  <span className="sn__bring-copied" aria-live="polite">Copied</span>
                )}
              </div>
              <p className="sn__bring-desc">{COPY_FRAMEWORK_MAPPING_ADDON_DESC}</p>
            </div>
            <p className="sn__bring-desc">{BRING_STORY_SHEET_UPLOAD_HINT}</p>
          </div>
        )}
      </div>

      {error && <p className="sn__error" role="alert">{error}</p>}
      {loading && <p className="sn__state">Loading docs…</p>}

      {!loading && (
        <ol className="sn__list">
          {(batches || []).map((batch) => (
            <li key={batch.batch_id}>
              <article className="sn__card">
                {(batch.documents || []).length === 0 ? (
                  <p className="sn__empty-batch">A batch landed here with no readable files.</p>
                ) : (
                  <ul className="sn__files">
                    {(batch.documents || []).map((doc) => (
                      <li key={doc.document_id} className="sn__file">
                        <div className="sn__file-meta">
                          <span className="sn__filename">{doc.original_filename}</span>
                          <span className="sn__format">{formatLabel(doc.declared_format)}</span>
                          {formatFileWordCount(doc.word_count) && (
                            <span className="sn__format">{formatFileWordCount(doc.word_count)}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="sn__open"
                          onClick={() => {
                            const label = doc.original_filename || 'Doc';
                            publishChatAttachment({
                              kind: 'doc',
                              id: doc.document_id,
                              label,
                              storyId,
                            });
                            publishChatFocus(label, '');
                          }}
                        >
                          {BRAINSTORM_IN_CHAT}
                        </button>
                        {doc.first_chunk_id ? (
                          <button
                            type="button"
                            className="sn__open"
                            onClick={() => openImportSourceByChunkId(doc.first_chunk_id)}
                          >
                            Open source
                          </button>
                        ) : (
                          <span className="sn__parked">No chunks to open</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="sn__status">{batchStatus(batch, approvedIds)}</p>
                {!batch.approved && !approvedIds.has(batch.batch_id) && batchHasDigest(batch) && (
                  <button
                    type="button"
                    className="sn__approve"
                    onClick={() => approveBatch(batch)}
                    disabled={approvingId === batch.batch_id}
                  >
                    {approvingId === batch.batch_id ? 'Saving…' : 'Approve digest'}
                  </button>
                )}
                {approveError?.batchId === batch.batch_id && (
                  <p className="sn__error" role="alert">{approveError.message}</p>
                )}
              </article>
            </li>
          ))}
        </ol>
      )}

      {!loading && (batches || []).length === 0 && (
        <p className="sn__state">{NOTES_EMPTY}</p>
      )}

      <div className="sn__doors">
        <button type="button" className="sn__add" onClick={() => setShowUpload(true)}>
          {uploadLabel || 'Upload docs'}
        </button>
        {project?.id && (
          <Link
            className="sn__make"
            to={docsCharacterOffersPath({ projectId: project.id, storyId })}
          >
            {MAKE_NOTES_FROM_DOCS}
          </Link>
        )}
      </div>

      {showUpload && project && (
        <ImportUploadModal
          project={project}
          storyId={storyId || null}
          onClose={closeUpload}
        />
      )}
    </section>
  );
}
